import React, { FormEvent } from "react";
import { UploadPurchaseOrdersFormData, UploadPurchaseOrdersFormDataKey } from "src/types";
import { ValidationErrors } from "src/errors";
import { PurchaseOrderUploadTooltip } from "src/components/tooltips/purchaseOrdersTooltips";
import { FormLvlErrorMessage, ValidationErrorMessage } from "src/components/ui/feedback";
import { SingleFileUpload } from "src/components/ui/inputs";
import { useScrollOnMessage } from "src/hooks";
import { CollapsibleSection } from "src/components/ui/collapsible";

interface UploadPurchaseOrdersFormProps {
  formData: UploadPurchaseOrdersFormData;
  validationErrors?: ValidationErrors<UploadPurchaseOrdersFormDataKey>;
  formLvlErrorMessage: string;
  handleFileUpload: (file: File | null) => void;
  handleSubmit: (e: FormEvent) => void;
}

const UploadPurchaseOrdersForm: React.FC<UploadPurchaseOrdersFormProps> = ({
  formData,
  validationErrors,
  formLvlErrorMessage,
  handleFileUpload,
  handleSubmit
}) => {
  const formLvlErrorMessageRef = useScrollOnMessage(formLvlErrorMessage);

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary-50 to-primary-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto">
        <div className="bg-white rounded-2xl shadow-soft overflow-hidden">
          {/* Header Section */}
          <div className="bg-black px-8 py-6">
            <h1 className="text-3xl font-bold text-white tracking-tight">Upload Purchase Orders</h1>
            <p className="text-primary-100 mt-2 text-lg">Import purchase orders from an Excel file</p>
          </div>

          <form onSubmit={handleSubmit} autoComplete="off" className="p-8">
            <div className="space-y-8">
              {/* File Upload Card */}
              <div className="bg-white border border-secondary-200 rounded-xl shadow-soft overflow-hidden">
                <CollapsibleSection
                  initiallyOpen={true}
                  title={
                    <h3 className="text-lg font-semibold text-secondary-800 flex items-center">
                      <svg
                        className="w-5 h-5 mr-2 text-primary-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                        />
                      </svg>
                      File Upload
                    </h3>
                  }
                >
                  <div className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-secondary-700 mb-3">
                          Purchase Orders File (.xlsx)
                        </label>
                        <div className="bg-secondary-50 border-2 border-dashed border-secondary-300 rounded-lg p-6 hover:border-primary-400 transition-colors duration-200">
                          <div className="flex flex-col items-center justify-center text-center">
                            <svg
                              className="w-12 h-12 text-secondary-400 mb-3"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                              />
                            </svg>
                            <SingleFileUpload
                              uploadText="Click to upload Excel file"
                              uploadedFile={formData.fileUpload}
                              onFileChange={handleFileUpload}
                              fileFormat=".xlsx"
                            />
                            <p className="text-xs text-secondary-500 mt-2">Accepted format: Excel (.xlsx) files only</p>
                          </div>
                        </div>
                        <ValidationErrorMessage
                          message={validationErrors?.getErrorMessageByFormDataKey("fileUpload")}
                        />
                      </div>
                    </div>
                  </div>
                </CollapsibleSection>
              </div>
            </div>

            {/* Form Level Error Message */}
            <div className="mt-6">
              <FormLvlErrorMessage message={formLvlErrorMessage} centred={false} ref={formLvlErrorMessageRef} />
            </div>

            {/* Action Buttons */}
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-start items-center pt-6 border-t border-secondary-200">
              <button
                className={`px-8 py-3 rounded-lg font-semibold text-white transition-all duration-200 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                  !formData.fileUpload
                    ? "bg-secondary-400 cursor-not-allowed"
                    : "bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 focus:ring-primary-500 shadow-lg hover:shadow-xl"
                }`}
                disabled={!formData.fileUpload}
                type="submit"
              >
                <span className="flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                  Upload Purchase Orders
                </span>
              </button>
              <PurchaseOrderUploadTooltip />
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export { UploadPurchaseOrdersForm };
